import { deriveScenesFromBody } from '@/features/studio/script/derive-scenes'
import { findSceneRangeInBody } from '@/features/studio/script/element-body-range'
import type { ScriptConvention } from '@/features/studio/script/useScriptFormatPrefs'

export type ScriptCommentAnchor = {
  start: number
  end: number
  quote?: string | null
}

export type ScriptCommentSelection = ScriptCommentAnchor & {
  episodeNo: number
  sceneNo?: number
}

export function readTextareaSelection(ta: HTMLTextAreaElement): ScriptCommentAnchor | null {
  const start = ta.selectionStart ?? 0
  const end = ta.selectionEnd ?? start
  if (start === end) return null
  return { start, end, quote: ta.value.slice(start, end) }
}

export function sceneNoAtOffset(
  body: string,
  offset: number,
  convention: ScriptConvention,
): number | undefined {
  const scenes = deriveScenesFromBody(body, convention)
  if (!scenes.length) return undefined
  for (const scene of scenes) {
    const range = findSceneRangeInBody(
      body,
      scenes.map((s) => ({ scene_no: s.scene_no, heading: s.heading, body: s.body })),
      scene.scene_no,
      convention,
    )
    if (range && offset >= range.start && offset < range.end) {
      return scene.scene_no
    }
  }
  return scenes[0]?.scene_no
}

export function resolveAnchorRange(
  body: string,
  anchor: ScriptCommentAnchor,
): { start: number; end: number } | null {
  const start = Math.max(0, anchor.start)
  const end = Math.max(start, anchor.end)
  const quote = anchor.quote?.trim()

  if (quote && body.slice(start, end) === quote) {
    return { start, end }
  }

  if (quote) {
    const exact = body.indexOf(quote)
    if (exact >= 0) {
      return { start: exact, end: exact + quote.length }
    }

    const windowStart = Math.max(0, start - 300)
    const windowEnd = Math.min(body.length, end + 300)
    const localIdx = body.slice(windowStart, windowEnd).indexOf(quote)
    if (localIdx >= 0) {
      const resolvedStart = windowStart + localIdx
      return { start: resolvedStart, end: resolvedStart + quote.length }
    }
  }

  if (end <= body.length && start < end) {
    return { start, end }
  }

  return null
}

export function scrollTextareaToRange(
  ta: HTMLTextAreaElement,
  start: number,
  end: number,
  scrollRoot?: HTMLElement | null,
) {
  ta.focus()
  ta.setSelectionRange(start, end)

  const textBefore = ta.value.slice(0, start)
  const lineCount = textBefore.split('\n').length
  const lineHeight = Number.parseFloat(getComputedStyle(ta).lineHeight) || 20
  ta.scrollTop = Math.max(0, (lineCount - 4) * lineHeight)

  const section = ta.closest('[data-episode-no]') as HTMLElement | null
  if (section && scrollRoot) {
    const sectionTop = section.offsetTop
    const targetTop = sectionTop + ta.scrollTop
    scrollRoot.scrollTo({ top: Math.max(0, targetTop - 80), behavior: 'smooth' })
  } else if (section) {
    section.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }
}

export function flashTextarea(ta: HTMLTextAreaElement) {
  ta.classList.remove('script-comment-flash')
  void ta.offsetWidth
  ta.classList.add('script-comment-flash')
  window.setTimeout(() => ta.classList.remove('script-comment-flash'), 1400)
}

export function truncateQuote(text: string, max = 72): string {
  const normalized = text.replace(/\s+/g, ' ').trim()
  if (normalized.length <= max) return normalized
  return `${normalized.slice(0, max - 1)}…`
}
